import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { supabase } from "../../lib/supabase";
import { extractRawTime, formatTime12h } from "../../lib/timeUtils";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import type { Deal, TeamMember } from "../../lib/types";
import CompleteModal from "./actions/CompleteModal";
import RescheduleModal from "./actions/RescheduleModal";
import NearbyBuildsModal from "./actions/NearbyBuildsModal";
import RescheduleBuildModal from "./actions/RescheduleBuildModal";
import RouteMap from "../shared/RouteMap";
import type { RouteStop } from "../shared/RouteMap";

import DealLink from "../DealLink";
import DealCallText from "../DealCallText";
import DispatchQuestionPill from "./comms/DispatchQuestionPill";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
import { addDaysISO, buildCovers, buildDayLabel, BUILD_SPAN_LOOKBACK_DAYS } from "../../lib/buildCalendar";
/** A single scheduled stop for today, normalized across source tables. */
type Stop = {
  key: string;
  type: "pre_inspection" | "adjuster_meeting" | "reinspection_adjuster" | "build";
  time: string | null; // HH:mm (nullable for all-day builds)
  deal: Deal;
  assigned: TeamMember | null;
};

const TYPE_LABEL: Record<Stop["type"], string> = {
  pre_inspection: "Pre-inspection",
  adjuster_meeting: "Adjuster meeting",
  reinspection_adjuster: "2nd Adjuster (reinsp.)",
  build: "Build",
};

const TYPE_CHIP: Record<Stop["type"], string> = {
  pre_inspection: "bg-amber-100 text-amber-800",
  adjuster_meeting: "bg-indigo-100 text-indigo-800",
  reinspection_adjuster: "bg-purple-100 text-purple-800",
  build: "bg-emerald-100 text-emerald-800",
};

const DEAL_COLS =
  "id,homeowner_name,homeowner_phone,homeowner_email,property_street,property_city,property_state,property_zip,property_lat,property_lon,claim_number,adjuster_meeting_date,adjuster_meeting_outcome,adjuster_name,adjuster_phone,owner_id,split_rm_id,stage,phase,days_in_stage,build_scheduled_date,build_end_date,build_start_date,build_completed_date,build_reschedule_count,build_reschedule_reason,build_notes,pre_inspection_date,pre_inspection_completed,pre_inspection_completed_date,pre_inspection_findings,pre_inspection_assigned_to,pre_inspection_assigned_date,assigned_rs_id,rs_assignment_date,ho_preferred_build_date,material_order_date,material_delivery_date,reinspection_adjuster_meeting_date,hover_link,scope_document_url,imging_report_link,sr_weather_report_storage_path,insurance_company";

function formatAddress(d: Deal) {
  const parts = [
    d.property_street,
    [d.property_city, d.property_state].filter(Boolean).join(", "),
    d.property_zip,
  ].filter(Boolean);
  return parts.join(" ");
}

/**
 * @param highlightRsId  When set (RS board), stops assigned to this roof
 *   specialist get a "YOU" badge + a "Yours" KPI tile. Omitted on the shared
 *   Robert/Jennifer/Executive views, where the tab behaves exactly as before.
 */
export default function TodayTab({ highlightRsId }: { highlightRsId?: string } = {}) {
  const today = useMemo(() => new Date(), []);
  const todayISO = format(today, "yyyy-MM-dd");
  const dayStart = `${todayISO}T00:00:00Z`;
  const dayEnd = `${todayISO}T23:59:59Z`;

  const [stops, setStops] = useState<Stop[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Action modal state — one modal open at a time, keyed by stop
  const [completeFor, setCompleteFor] = useState<Stop | null>(null);
  const [rescheduleFor, setRescheduleFor] = useState<Stop | null>(null);
  // "Nearby builds" — the map of roofs we've built around this stop (show the adjuster)
  const [nearbyFor, setNearbyFor] = useState<Stop | null>(null);
  // Builds get the real Reschedule build flow (Oct 16, 2026): deal + Google
  // event + SRS + homeowner text + RM, all from the server.
  const [rescheduleBuildFor, setRescheduleBuildFor] = useState<Stop | null>(null);

  // Route-building selection state
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggleSelected = useCallback((key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (!stops) return;
    setSelected(new Set(stops.map((s) => s.key)));
  }, [stops]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Real-time: auto-refresh when deals or dispatch_schedule change
  useRealtimeTable("deals", reload);
  useRealtimeTable("dispatch_schedule", reload);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setError(null);

      const [{ data: adj, error: e1 }, { data: pre, error: e2 }, { data: builds, error: e3 }, { data: reinspAdj, error: e4 }] =
        await Promise.all([
          supabase
            .from("deals")
            .select(DEAL_COLS)
            .gte("adjuster_meeting_date", dayStart)
            .lte("adjuster_meeting_date", dayEnd),
          supabase
            .from("deals")
            .select(DEAL_COLS)
            .gte("pre_inspection_date", dayStart)
            .lte("pre_inspection_date", dayEnd),
          // A build that started earlier and runs through today shows too
          // (Oct 26, 2026: builds can run more than one day). Filtered below.
          supabase
            .from("deals")
            .select(DEAL_COLS)
            .gte("build_scheduled_date", addDaysISO(todayISO, -BUILD_SPAN_LOOKBACK_DAYS))
            .lte("build_scheduled_date", todayISO),
          supabase
            .from("deals")
            .select(DEAL_COLS)
            .gte("reinspection_adjuster_meeting_date", dayStart)
            .lte("reinspection_adjuster_meeting_date", dayEnd),
        ]);

      const err = e1 ?? e2 ?? e3 ?? e4;
      if (err) {
        if (!cancelled) setError(err.message);
        return;
      }

      const memberIds = new Set<string>();
      (adj ?? []).forEach((d) => d.owner_id && memberIds.add(d.owner_id));
      (pre ?? []).forEach(
        (d) =>
          d.pre_inspection_assigned_to &&
          memberIds.add(d.pre_inspection_assigned_to),
      );
      (builds ?? []).forEach((d) => buildCovers(d.build_scheduled_date, d.build_end_date, todayISO) && d.owner_id && memberIds.add(d.owner_id));
      (reinspAdj ?? []).forEach((d) => d.owner_id && memberIds.add(d.owner_id));

      const members: Record<string, TeamMember> = {};
      if (memberIds.size) {
        const { data: ms } = await supabase
          .from("team_roster")
          .select("*")
          .in("id", [...memberIds]);
        (ms ?? []).forEach((m) => {
          members[m.id] = m as TeamMember;
        });
      }

      const rows: Stop[] = [];
      (adj ?? []).forEach((d) =>
        rows.push({
          key: `adj-${d.id}`,
          type: "adjuster_meeting",
          time: extractRawTime(d.adjuster_meeting_date),
          deal: d as Deal,
          assigned: d.owner_id ? members[d.owner_id] ?? null : null,
        }),
      );
      (pre ?? []).forEach((d) =>
        rows.push({
          key: `pre-${d.id}`,
          type: "pre_inspection",
          time: extractRawTime(d.pre_inspection_date),
          deal: d as Deal,
          assigned: d.pre_inspection_assigned_to
            ? members[d.pre_inspection_assigned_to] ?? null
            : null,
        }),
      );
      (reinspAdj ?? []).forEach((d) =>
        rows.push({
          key: `reinsp-adj-${d.id}`,
          type: "reinspection_adjuster",
          time: extractRawTime(d.reinspection_adjuster_meeting_date),
          deal: d as Deal,
          assigned: d.owner_id ? members[d.owner_id] ?? null : null,
        }),
      );
      const buildsToday = (builds ?? []).filter((d) => buildCovers(d.build_scheduled_date, d.build_end_date, todayISO));
      buildsToday.forEach((d) =>
        rows.push({
          key: `build-${d.id}`,
          type: "build",
          time: null,
          deal: d as Deal,
          assigned: d.owner_id ? members[d.owner_id] ?? null : null,
        }),
      );

      rows.sort((a, b) => {
        if (a.time && b.time) return a.time.localeCompare(b.time);
        if (a.time) return -1;
        if (b.time) return 1;
        return a.type.localeCompare(b.type);
      });

      if (!cancelled) setStops(rows);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [todayISO, dayStart, dayEnd, reloadKey]);

  // Is this stop assigned to the RS whose board we're viewing? Adjuster
  // meetings / builds / reinspections key off assigned_rs_id; pre-inspections
  // can also be keyed off pre_inspection_assigned_to (ScheduleTab mirrors the
  // two). Returns false on the shared (non-RS) views where highlightRsId is unset.
  const stopIsMine = useCallback(
    (s: Stop) =>
      !!highlightRsId &&
      (s.deal.assigned_rs_id === highlightRsId ||
        (s.type === "pre_inspection" &&
          s.deal.pre_inspection_assigned_to === highlightRsId)),
    [highlightRsId],
  );

  const mineCount = useMemo(
    () => (stops && highlightRsId ? stops.filter(stopIsMine).length : 0),
    [stops, highlightRsId, stopIsMine],
  );

  // Type-based counts for KPI row (must be before conditional returns — Rules of Hooks)
  const typeCounts = useMemo(() => {
    if (!stops) return { adj: 0, pre: 0, build: 0, reinsp: 0 };
    return {
      adj: stops.filter((s) => s.type === "adjuster_meeting").length,
      pre: stops.filter((s) => s.type === "pre_inspection").length,
      build: stops.filter((s) => s.type === "build").length,
      reinsp: stops.filter((s) => s.type === "reinspection_adjuster").length,
    };
  }, [stops]);

  // Build route map stops — if some are selected, only show those;
  // if none are selected, show all (backwards-compatible default).
  const TYPE_PIN: Record<Stop["type"], string> = {
    pre_inspection: "P",
    adjuster_meeting: "A",
    reinspection_adjuster: "R",
    build: "B",
  };

  const routeStops: RouteStop[] = useMemo(() => {
    if (!stops) return [];
    const source = selected.size > 0
      ? stops.filter((s) => selected.has(s.key))
      : stops;
    return source
      .filter(
        (s) =>
          s.deal.property_lat != null && s.deal.property_lon != null,
      )
      .map((s, i) => ({
        label: TYPE_PIN[s.type] ?? String(i + 1),
        name: `${TYPE_LABEL[s.type]} — ${s.deal.homeowner_name ?? "(no name)"}`,
        address: formatAddress(s.deal),
        lat: s.deal.property_lat!,
        lon: s.deal.property_lon!,
      }));
  }, [stops, selected]);

  // Google Maps route link for selected stops — uses lat/lon when available, falls back to address
  const selectedGoogleRoute = useMemo(() => {
    if (selected.size === 0 || !stops) return "";
    const selectedStops = stops.filter((s) => selected.has(s.key));
    if (selectedStops.length === 0) return "";
    const waypoints = selectedStops.map((s) => {
      if (s.deal.property_lat != null && s.deal.property_lon != null)
        return `${s.deal.property_lat},${s.deal.property_lon}`;
      return encodeURIComponent(formatAddress(s.deal));
    });
    if (waypoints.length === 1)
      return `https://www.google.com/maps/dir/?api=1&destination=${waypoints[0]}&travelmode=driving`;
    const origin = waypoints[0];
    const dest = waypoints[waypoints.length - 1];
    const mid = waypoints.slice(1, -1).join("/");
    return mid
      ? `https://www.google.com/maps/dir/${origin}/${mid}/${dest}`
      : `https://www.google.com/maps/dir/${origin}/${dest}`;
  }, [stops, selected]);

  if (error)
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded">
        Couldn't load today's schedule — {error}
      </div>
    );
  if (!stops)
    return <div className="text-slate-500 p-4">Loading today's schedule…</div>;

  return (
    <div>
      {/* KPI Row */}
      <div className={"grid grid-cols-2 gap-2 mb-3 " + (highlightRsId ? "md:grid-cols-6" : "md:grid-cols-5")}>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-slate-900">{stops.length}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Total Stops</div>
        </div>
        {highlightRsId && (
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-2.5 text-center">
            <div className="text-xl font-extrabold text-teal-700">{mineCount}</div>
            <div className="text-[9px] font-semibold text-teal-600 uppercase tracking-wide">Yours</div>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-indigo-600">{typeCounts.adj}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Adjuster</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-amber-600">{typeCounts.pre}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Pre-Insp</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-emerald-600">{typeCounts.build}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Builds</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-purple-600">{typeCounts.reinsp}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">2nd Adj</div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12px] text-slate-600 font-medium">
          {format(today, "EEEE, MMMM d")}
          {selected.size > 0 && (
            <span className="ml-2 text-teal-600 font-semibold">
              · {selected.size} selected
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {selected.size < stops.length ? (
            <button
              onClick={selectAll}
              className="px-2.5 py-1 rounded-md bg-white text-slate-500 text-[11px] font-semibold border border-slate-200 hover:bg-slate-50"
            >
              Select all
            </button>
          ) : (
            <button
              onClick={clearSelection}
              className="px-2.5 py-1 rounded-md bg-white text-slate-500 text-[11px] font-semibold border border-slate-200 hover:bg-slate-50"
            >
              Clear all
            </button>
          )}
          {selected.size > 0 && selectedGoogleRoute && (
            <a
              href={selectedGoogleRoute}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1 rounded-md bg-teal-500 text-white text-[11px] font-semibold hover:bg-teal-600 flex items-center gap-1.5 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
              Open Route ({selected.size} stops)
            </a>
          )}
        </div>
      </div>

      {/* Map Hero — always visible, matches artifact gradient style */}
      {routeStops.length > 0 ? (
        <RouteMap stops={routeStops} minutesPerStop={0} perStopLabel="" />
      ) : (
        <div className="mb-4 relative rounded-xl overflow-hidden border border-slate-200 h-48"
          style={{ background: "linear-gradient(135deg, #e0f2fe 0%, #f0fdf4 50%, #fefce8 100%)" }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-500">
            <span className="text-3xl">🗺️</span>
            <span className="text-[12px] font-medium">
              {selected.size > 0
                ? `${selected.size} stop${selected.size > 1 ? "s" : ""} selected — no coordinates available`
                : "Select stops below to build your route"}
            </span>
            {selected.size > 0 && selectedGoogleRoute && (
              <a
                href={selectedGoogleRoute}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 rounded-lg bg-teal-500 text-white text-[12px] font-semibold hover:bg-teal-600 transition shadow-sm"
              >
                🗺️ Open Route in Maps
              </a>
            )}
          </div>
        </div>
      )}

      {stops.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-slate-500">
          Nothing scheduled today.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {stops.map((s, idx) => {
            const isSelected = selected.has(s.key);
            const mine = stopIsMine(s);
            return (
              <div
                key={s.key}
                className={
                  // Phones stack each stop into two lines: name + time, then the
                  // Nearby builds pill + the action strip (the basis-full block
                  // below). md+ is one line, exactly as before.
                  "flex flex-wrap md:flex-nowrap items-center gap-x-2 gap-y-1.5 px-2 md:px-3 py-2.5 border-b border-slate-100 last:border-b-0 cursor-pointer transition-all text-[12px] " +
                  (isSelected
                    ? "bg-teal-50 border-l-[3px] border-l-teal-500"
                    : mine
                    ? "bg-amber-50/50 border-l-[3px] border-l-amber-400 hover:bg-amber-50"
                    : "hover:bg-teal-50/50")
                }
                onClick={() => toggleSelected(s.key)}
              >
                {/* Numbered circle */}
                <div className="w-[22px] h-[22px] rounded-full bg-teal-500 text-white text-[10px] font-extrabold flex items-center justify-center shrink-0">
                  {idx + 1}
                </div>

                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelected(s.key)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-3.5 h-3.5 rounded border-slate-300 text-teal-500 focus:ring-teal-500 cursor-pointer shrink-0 accent-teal-500"
                />

                {/* Type badge */}
                <span className={"px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 " + TYPE_CHIP[s.type]}>
                  {TYPE_LABEL[s.type].replace("Adjuster meeting", "ADJ").replace("Pre-inspection", "PRE").replace("2nd Adjuster (reinsp.)", "2ND ADJ").replace("Build", "BUILD")}
                </span>

                {/* Name + address */}
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-slate-900">
                    <DealLink id={s.deal.id}>{s.deal.homeowner_name ?? "(no name)"}</DealLink>
                  </span>
                  {isAddressLinkable(s.deal) ? (
                    <a href={mapsHref(s.deal)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-slate-500 text-[11px] ml-1.5 hidden md:inline hover:underline hover:text-slate-700">
                      {formatAddress(s.deal) || "(no address)"}
                    </a>
                  ) : (
                    <span className="text-slate-500 text-[11px] ml-1.5 hidden md:inline">
                      {formatAddress(s.deal) || "(no address)"}
                    </span>
                  )}
                </div>

                {/* YOU tag — assigned to the RS whose board this is */}
                {mine && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-amber-400 text-amber-950 shrink-0 tracking-wide">
                    YOU
                  </span>
                )}

                {/* RM / Assigned */}
                <span className="text-slate-500 text-[11px] hidden md:inline shrink-0">
                  {s.assigned ? s.assigned.full_name.split(" ")[0] : "—"}
                </span>

                {/* Time */}
                <span className="text-slate-500 text-[11px] ml-auto shrink-0">
                  {s.time ? formatTime12h(s.time) : s.type === "build" ? (buildDayLabel(s.deal.build_scheduled_date, s.deal.build_end_date, todayISO) || "All Day") : "—"}
                </span>

                {/* Second line on phones (basis-full wraps it under the name); inline on md+ */}
                <div className="basis-full md:basis-auto flex flex-wrap md:flex-nowrap items-center gap-x-2 gap-y-1.5 md:ml-1 md:shrink-0">
                  {/* Nearby builds — a labeled pill on phones, where it has a line of its own.
                      On md+ it is the round 🏘 button in the action strip below. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setNearbyFor(s); }}
                    className="md:hidden inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-full border border-emerald-200 bg-white px-1.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-50 transition"
                    title="Roofs we've built within 5 mi — show the adjuster"
                  >
                    <span aria-hidden="true">🏘</span>
                    Nearby builds
                  </button>

                  {/* Quick action buttons — icon-style matching artifact */}
                  <div className="flex gap-1 shrink-0 ml-auto md:ml-0 items-center">
                    <a
                      href={mapsHref(s.deal)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[12px] hover:bg-slate-50 transition"
                      title="Navigate"
                    >
                      📍
                    </a>
                    <DealCallText dealId={s.deal.id} homeownerName={s.deal.homeowner_name} homeownerPhone={s.deal.homeowner_phone} compact />
                    <DispatchQuestionPill
                      deal={{
                        id: s.deal.id,
                        homeowner_name: s.deal.homeowner_name,
                        property_street: s.deal.property_street,
                        owner_id: s.deal.owner_id,
                        split_rm_id: s.deal.split_rm_id ?? null,
                        pre_inspection_assigned_to: s.deal.pre_inspection_assigned_to ?? null,
                        phase: s.deal.phase ?? null,
                      }}
                    />
                    {s.type === "build" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setRescheduleBuildFor(s); }}
                        className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center text-[12px] hover:bg-amber-600 transition"
                        title="Reschedule build"
                      >
                        🗓
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setNearbyFor(s); }}
                      className="hidden md:flex w-7 h-7 rounded-full bg-white border border-emerald-200 text-emerald-700 items-center justify-center text-[12px] hover:bg-emerald-50 transition"
                      title="Nearby builds — roofs we've built within 5 mi"
                    >
                      🏘
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCompleteFor(s); }}
                      className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[12px] hover:bg-emerald-600 transition"
                      title="Complete"
                    >
                      ✓
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {completeFor && (
        <CompleteModal
          open
          deal={completeFor.deal}
          type={completeFor.type}
          onClose={() => setCompleteFor(null)}
          onCompleted={reload}
        />
      )}
      {rescheduleFor && (
        <RescheduleModal
          open
          deal={rescheduleFor.deal}
          type={rescheduleFor.type}
          onClose={() => setRescheduleFor(null)}
          onCompleted={reload}
        />
      )}
      {rescheduleBuildFor && (
        <RescheduleBuildModal
          open
          deal={rescheduleBuildFor.deal}
          onClose={() => setRescheduleBuildFor(null)}
          onDone={reload}
        />
      )}
      {nearbyFor && (
        <NearbyBuildsModal deal={nearbyFor.deal} onClose={() => setNearbyFor(null)} />
      )}
    </div>
  );
}
